/**
 * Build PDF / report summary metrics aligned with the dashboard API and chart datasets.
 * The /api/dashboard_summary view does not include per-domain CRM/blank/duplicate counts;
 * those are derived here from date-filtered dashboard arrays so PDFs stay consistent with the selected range.
 */

import { effectiveWithinRpdDynamic } from './chartDataTransform';

/**
 * @param {Object} filteredData - Output of filterDashboardDataByDate
 * @param {Object|null} apiSummary - Row from getDashboardSummary() (optional)
 * @returns {Object} Fields consumed by PrintSummaryStats
 */
export function buildReportSummaryForPrint(filteredData = {}, apiSummary = null) {
  const controlRows = (filteredData.controlStandards || []).filter(
    (item) =>
      item.certified_value != null &&
      item.certified_value !== undefined &&
      item.z_score != null &&
      item.z_score !== undefined
  );

  const control_standards_count = controlRows.length;
  const control_standards_pass_2sd = controlRows.filter((i) => i.within_2sd).length;
  const control_standards_fail_3sd = controlRows.filter((i) => !i.within_3sd).length;

  const blankRows = filteredData.blanksDrift || [];
  const blank_samples_count = blankRows.length;
  const blank_samples_fail = blankRows.filter((b) =>
    String(b.qc_status || '').toUpperCase().startsWith('FAIL')
  ).length;
  const blank_samples_pass = blank_samples_count - blank_samples_fail;

  const dupRows = filteredData.duplicatesDrift || [];
  const field_duplicates_count = dupRows.length;
  const field_duplicates_pass = dupRows.filter((r) => effectiveWithinRpdDynamic(r)).length;
  const field_duplicates_fail = dupRows.filter((r) => !effectiveWithinRpdDynamic(r)).length;

  const reconcile = (filteredData.sampleCountReconcile || []).filter(
    (r) => r.status === 'Finalized'
  );
  const totalFromReconcile = reconcile.reduce(
    (sum, r) => sum + (Number(r.actual_count) || 0),
    0
  );
  const total_samples = reconcile.length > 0 ? totalFromReconcile : null;

  const failures = filteredData.qaqcFailures || [];
  const unresolved_from_failures = failures.filter((f) => !f.resolved).length;

  return {
    total_samples,
    unresolved_failures:
      failures.length > 0
        ? unresolved_from_failures
        : apiSummary?.unresolved_failures ?? unresolved_from_failures,

    control_standards_count,
    control_standards_pass_2sd,
    control_standards_fail_3sd,

    blank_samples_count,
    blank_samples_pass,
    blank_samples_fail,

    field_duplicates_count,
    field_duplicates_pass,
    field_duplicates_fail,

    _meta: {
      generated_from: 'reportSummaryStats',
      api_snapshot: apiSummary?.generated_at || null
    }
  };
}
